import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RefObject } from 'react';
import { useAppSelector } from '../../hooks/redux';
import { useAIClassification } from '../../hooks/useAIClassification';
import {
  answerQuestion,
  questionnaireStepBack,
  startAiSystem,
  updateAiSystem,
  generateQuestionnaireReportPdf
} from '../../services/AISystemsService';
import { donwloadAIPreliminaryReport } from '../../services/AIClassificationService';
import {
  getCurrentStepId,
  getPreviousStepId,
  setPreviousQuestions,
  setShowPreStep,
  setPreStepPart,
  setStartAiSystemBodyResponse,
  stepBack,
  stepNext,
  updateStepBasedOnBackend,
  setCreatingAiSystem,
  setStepType,
  setShowEmptyState,
  setPreStepInputError,
  setNextQuestion,
  setIsFinalizingAIReport,
  setQuestionnaireServerErrorStatus,
  clearQuestionnaireServerError
} from '../../store/slices/stepperSlice';
import { Button } from '../ui/Button/Button';
import { setAISystemStatus } from '../../store/slices/aiSystemsSlice';
import ProvideClarification from '../ui/Questionaire/ProvideClarification';
import {
  isAnalyzingDescription,
  isLoadingNextQuestion,
  isEditingOptionAnswer,
  shouldShowClarification,
  setEditAnswerClarificationContext,
  setCurrentAnswer,
  setIsEditingQuestionnaireItem
} from '../../store/slices/aiPoweredSlice';
import { PrimaryButton } from '../ui/Button/Buttons';
import { DownloadIcon } from '../../icons/SvgIcons';
import { useClarificationTextareaMaxHeight } from '../../hooks/useClarificationTextareaMaxHeight';
import { getApiErrorStatus, isServerErrorStatus } from '../../utils/apiError';

type QuestionnaireFooterProps = {
  questionnaireContentRef?: RefObject<HTMLDivElement | null>;
};

export function QuestionnaireFooter({
  questionnaireContentRef
}: QuestionnaireFooterProps) {
  const {
    startAiSystemBodyRequest,
    steps,
    showPreStep,
    preStepPart,
    disableNextStep,
    answerRequestsInFlight,
    providerDeclarationChecked,
    questionnaireId,
    creatingAiSystem,
    preStepInputError,
    selectedEntity,
    aiSystemId,
    startAiSystemBodyResponse
  } = useAppSelector((state) => state.stepper);

  const {
    currentAnswer,
    submittingAnswer,
    isEditingQuestionnaireItem,
    editingAnswerIndex,
    followUpAnswers,
    rerunLoading,
    signLoading,
    currentJob,
    isEditClassificationPathEnabled
  } = useAppSelector((state) => state.aiPowered);

  const analyzingDescription = useAppSelector(isAnalyzingDescription);
  const loadingNextAiQuestion = useAppSelector(isLoadingNextQuestion);
  const editingOptionAnswer = useAppSelector(isEditingOptionAnswer);
  const showClarification = useAppSelector(shouldShowClarification);

  const { aiPowered } = useAIClassification(aiSystemId);

  const { submitFollowUpAnswer, rerunClassification, signClassification } =
    useAIClassification(aiSystemId, {
      enablePolling: false
    });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentStepId = getCurrentStepId(steps);
  const previousStepId = getPreviousStepId(steps);

  const isReportStep = currentStepId === 'REPORT';
  const isAIPoweredAssessment =
    startAiSystemBodyRequest.assessmentType === 'AI_POWERED';

  const hasPreStepErrors =
    !!preStepInputError && Object.values(preStepInputError).some(Boolean);

  const hasAIDeclaration = Boolean(
    currentJob?.finalReport?.declaration_text?.trim()
  );

  const disablePrimaryButton = () => {
    if (showPreStep) {
      return disableNextStep || creatingAiSystem || hasPreStepErrors;
    }

    if (isAIPoweredAssessment) {
      return (
        isEditClassificationPathEnabled ||
        signLoading ||
        (hasAIDeclaration && !providerDeclarationChecked)
      );
    }

    if (isReportStep) {
      return !providerDeclarationChecked;
    }

    return (
      disableNextStep ||
      answerRequestsInFlight > 0 ||
      creatingAiSystem ||
      hasPreStepErrors
    );
  };

  const clarificationLoading = editingOptionAnswer
    ? rerunLoading
    : submittingAnswer;

  const mapPreStepMessages = (messages: string[]) => ({
    name:
      messages.find((message) => message.includes('AI System Name')) ||
      undefined,
    version:
      messages.find((message) => message.includes('AI System Version')) ||
      undefined,
    organization:
      messages.find((message) => message.includes('Organization Name')) ||
      undefined,
    description:
      messages.find((message) => message.includes('Description')) || undefined,
    compliance:
      messages.find((message) =>
        message.includes('Compliance Contact Person')
      ) || undefined,
    entity: messages.find((message) => message.includes('Entity')) || undefined
  });

  const getErrorMessages = (error: any): string[] => {
    const messages = error.response?.data?.message;

    if (Array.isArray(messages)) return messages;
    if (typeof messages === 'string') return [messages];

    return [];
  };

  const handleButton = async (action: 'back' | 'next') => {
    if (action === 'back') {
      if (selectedEntity === 'authorised-representative') {
        dispatch(stepBack());
        return;
      }

      try {
        dispatch(clearQuestionnaireServerError());
        const response = await questionnaireStepBack(
          questionnaireId ?? '',
          previousStepId
        );

        if (response.questions.length === 0) {
          dispatch(setShowEmptyState(true));
        } else {
          dispatch(setShowEmptyState(false));
        }

        dispatch(stepBack());
        dispatch(updateStepBasedOnBackend(response.step));
        dispatch(setStepType(response.type));
        dispatch(setPreviousQuestions(response.questions));
      } catch (error) {
        const status = getApiErrorStatus(error);

        if (isServerErrorStatus(status)) {
          dispatch(setQuestionnaireServerErrorStatus(status));
        }
      }
      return;
    }

    if (showPreStep) {
      if (preStepPart === 'basicInformation') {
        try {
          dispatch(setCreatingAiSystem(true));
          dispatch(clearQuestionnaireServerError());
          const response = await startAiSystem(startAiSystemBodyRequest);

          dispatch(setStartAiSystemBodyResponse(response));
          dispatch(setPreStepInputError(null));

          // if assessment type is AI powered, skip entity classification pre-step and go directly to questionnaire
          if (response.assessmentType === 'AI_POWERED') {
            dispatch(setShowPreStep(false));
            dispatch(setAISystemStatus('draft'));
            return;
          }

          dispatch(setPreStepPart('entityClassification'));
        } catch (error: any) {
          const status = getApiErrorStatus(error);

          if (isServerErrorStatus(status)) {
            dispatch(setQuestionnaireServerErrorStatus(status));
          }

          dispatch(
            setPreStepInputError(mapPreStepMessages(getErrorMessages(error)))
          );
        } finally {
          dispatch(setCreatingAiSystem(false));
        }

        return;
      }

      if (
        !questionnaireId ||
        !selectedEntity ||
        !startAiSystemBodyResponse?.question
      ) {
        dispatch(
          setPreStepInputError({
            ...preStepInputError,
            entity: 'Please select an entity before continuing.'
          })
        );
        return;
      }

      try {
        dispatch(setCreatingAiSystem(true));
        dispatch(clearQuestionnaireServerError());
        const response = await answerQuestion(questionnaireId, {
          questionId: startAiSystemBodyResponse.question.id,
          answer: [selectedEntity]
        });
        if (!response) return;

        dispatch(setStepType(response.type));
        dispatch(setStartAiSystemBodyResponse(null));
        dispatch(setShowPreStep(false));
        dispatch(setAISystemStatus('draft'));
        dispatch(setPreStepInputError(null));

        if (response.type === 'question') {
          dispatch(setShowEmptyState(false));
          dispatch(setNextQuestion(response));
          dispatch(updateStepBasedOnBackend(response.question.step));
          return;
        }

        dispatch(setNextQuestion(null));
        dispatch(setShowEmptyState(true));
        dispatch(updateStepBasedOnBackend(response.step));
      } catch (error: any) {
        const status = getApiErrorStatus(error);

        if (isServerErrorStatus(status)) {
          dispatch(setQuestionnaireServerErrorStatus(status));
        }

        dispatch(
          setPreStepInputError(mapPreStepMessages(getErrorMessages(error)))
        );
      } finally {
        dispatch(setCreatingAiSystem(false));
      }
    } else {
      if (aiSystemId && currentStepId === 'REPORT') {
        try {
          await updateAiSystem(aiSystemId);
          dispatch(setAISystemStatus('created'));
          // Trigger PDF generation asynchronously
          generateQuestionnaireReportPdf(aiSystemId).catch((error) => {
            console.error('Failed to generate PDF report:', error);
          });

          navigate('/ai-systems', { state: { resetStepper: true } });
          return;
        } catch (error) {
          console.error('Failed to update AI system:', error);
          return;
        }
      }
      dispatch(stepNext());
    }
  };

  const handleClarificationSubmit = async () => {
    const trimmedAnswer = currentAnswer.trim();
    if (!trimmedAnswer || submittingAnswer) return;

    await submitFollowUpAnswer(trimmedAnswer);
  };

  const handleEditClarificationSubmit = async () => {
    const trimmedAnswer = currentAnswer.trim();
    if (
      !trimmedAnswer ||
      editingAnswerIndex === null ||
      rerunLoading ||
      trimmedAnswer === followUpAnswers[editingAnswerIndex]?.answer
    ) {
      return;
    }

    const nextFollowUpAnswers = followUpAnswers.map((answer, index) =>
      index === editingAnswerIndex
        ? { ...answer, answer: trimmedAnswer }
        : answer
    );

    await rerunClassification({
      regulation: 'EU_AI_ACT',
      followUpAnswers: nextFollowUpAnswers,
      editedAtIndex: editingAnswerIndex
    });
    dispatch(setIsEditingQuestionnaireItem(false));
    dispatch(setEditAnswerClarificationContext({ index: null, show: false }));
    dispatch(setCurrentAnswer(''));
  };

  const clarificationSubmit = editingOptionAnswer
    ? handleEditClarificationSubmit
    : handleClarificationSubmit;

  const clarificationMaxTextareaHeight = useClarificationTextareaMaxHeight({
    enabled: showClarification,
    questionnaireContentRef,
    recalculationDependencies: [editingAnswerIndex, isEditingQuestionnaireItem]
  });

  const isAIJobFinalized = currentJob?.processState === 'FINALIZED';
  const showAIFinalizedAction =
    isAIPoweredAssessment && !showPreStep && isAIJobFinalized;

  const aiFinalizedButtonText =
    currentJob?.tier === 'high'
      ? 'Finalize'
      : currentJob?.tier === 'medium'
        ? 'Download Preliminary Report'
        : null;

  const primaryButtonText = showAIFinalizedAction
    ? aiFinalizedButtonText
    : steps[steps.length - 1].status === 'current'
      ? 'Create AI System'
      : 'Save & Continue';

  const primaryButtonIcon =
    showAIFinalizedAction && currentJob?.tier === 'medium'
      ? DownloadIcon()
      : undefined;

  const handlePrimaryButton = async () => {
    if (!showAIFinalizedAction) {
      await handleButton('next');
      return;
    }

    if (currentJob?.tier === 'high') {
      dispatch(setIsFinalizingAIReport(true));

      const response = await signClassification(currentJob.jobId);

      if (response) {
        dispatch(setAISystemStatus('created'));
        navigate('/ai-systems', { state: { resetStepper: true } });
        return;
      }

      dispatch(setIsFinalizingAIReport(false));
      return;
    }

    if (currentJob?.tier === 'medium') {
      if (!aiSystemId) {
        return;
      }

      try {
        const blob = await donwloadAIPreliminaryReport(
          aiSystemId,
          currentJob.jobId
        );
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ai-preliminary-report-${currentJob.jobId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Failed to download AI preliminary report:', error);
      }
    }
  };

  if (
    analyzingDescription ||
    loadingNextAiQuestion ||
    (isEditingQuestionnaireItem && !showClarification) ||
    (showAIFinalizedAction && !aiFinalizedButtonText) ||
    aiPowered?.error
  )
    return null;

  return (
    <div
      className={`sticky bottom-0 flex justify-between pt-5 bg-white ${
        showClarification ? '' : 'border-t border-gray-150'
      }`}
    >
      {showClarification ? (
        <ProvideClarification
          value={currentAnswer}
          onChange={(value) => dispatch(setCurrentAnswer(value))}
          onSubmit={clarificationSubmit}
          disabled={!showClarification}
          loading={clarificationLoading}
          maxTextareaHeight={clarificationMaxTextareaHeight}
        />
      ) : (
        <>
          <Button
            onClick={() => handleButton('back')}
            buttonClassName={`flex items-center py-[9px] px-[17px] h-[38px] text-gray-700 text-sm font-medium border border-gray-300 rounded-full shadow-sm hover:bg-gray-250 hover:text-gray-700 outline-none ${
              steps[0].status === 'current' ? 'hidden' : ''
            }`}
          >
            Back
          </Button>
          <PrimaryButton
            onClick={handlePrimaryButton}
            disabled={disablePrimaryButton()}
            className="ml-auto"
            icon={primaryButtonIcon}
          >
            {primaryButtonText}
          </PrimaryButton>
        </>
      )}
    </div>
  );
}
